import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
    Box,
    Button,
    Checkbox,
    Container,
    Divider,
    List,
    ListItem,
    ListItemText,
    Stack,
} from '@mui/material'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { useInnsynContext } from '../../../../context/InnsynContextProvider'
import {
    INNSYN_SEARCH_OEREMERKE_DISTRIKT_PATH,
    INNSYN_SEARCH_OEREMERKE_PATH,
} from '../../../../paths'
import { FormattedMessage, useIntl } from 'react-intl'
import NavigationHeader from '../../../../components/NavigationHeader'
import '../../UseButton.scss'
import { getOmraadeNameKey } from '../../../../services/TextService'

export const SelectArea = () => {
    const navigate = useNavigate()
    const intl = useIntl()
    const [query, setQuery] = useSearchParams()
    const { geografi } = useInnsynContext()
    const areaStringArray = query.getAll('a')
    const districtStringArray = query.getAll('d')
    const siidaStringArray = query.getAll('s')
    const { state } = useLocation() as RightEar

    const areaContainingSiida = () => {
        const area = geografi?.filter(
            (area) =>
                area.distrikter?.filter(
                    (distrikt) =>
                        distrikt.siidaer.filter((siida) =>
                            siidaStringArray.includes(siida.siidaId.toString())
                        ).length > 0
                ).length > 0
        )
        return area?.[0]
    }

    const selectDisctrict = (areaId: number) => {
        ;((!areaStringArray.includes(areaId.toString()) &&
            areaId !== areaContainingSiida()?.omraadeId) ||
            (areaId === areaContainingSiida()?.omraadeId &&
                !areaStringArray.includes(areaId.toString()) &&
                siidaStringArray.length === 0)) &&
            query.append('a', areaId.toString())
        setQuery(query)
        navigate(
            {
                pathname: INNSYN_SEARCH_OEREMERKE_DISTRIKT_PATH(areaId),
                search: `?${query}`,
            },
            { state: { isRightEar: state?.isRightEar } }
        )
    }

    const handleChange = (input: number) => {
        if (!geografi) return
        if (areaStringArray.includes(input.toString())) {
            let areasToKeep = areaStringArray.filter(
                (it) => it !== input.toString()
            )
            const remainingAreas = geografi.filter((val) => {
                return areasToKeep.find((el) => {
                    return val.omraadeId === parseInt(el)
                })
            }) as Area[]
            const allDistrictsInRemainingAreas = remainingAreas
                .map((el) => el.distrikter)
                .flat()
            let districtsToKeep = districtStringArray.filter((districtId) =>
                allDistrictsInRemainingAreas.some(
                    (el) => el.distriktId === parseInt(districtId)
                )
            )
            query.delete('a')
            query.delete('d')
            if (input === areaContainingSiida()?.omraadeId) {
                query.delete('s')
            }
            areasToKeep.forEach((item) => query.append('a', item))
            districtsToKeep.forEach((item) => query.append('d', item))
        } else query.append('a', input.toString())
        setQuery(query, {
            replace: true,
            state: { isRightEar: state?.isRightEar },
        })
    }

    const getNumberOfSelectedDistrictsForArea = (area: Area) => {
        const districtsInArea = districtStringArray.filter(
            (districtIdCondition) =>
                area.distrikter
                    .map((district) => district.distriktId)
                    .includes(parseInt(districtIdCondition))
        )
        if (districtsInArea.length > 0) {
            if (
                area.omraadeId === areaContainingSiida()?.omraadeId &&
                siidaStringArray.length > 0
            ) {
                return districtsInArea.length + 1
            }
            return districtsInArea.length
        }
        if (areaStringArray.includes(area.omraadeId.toString())) {
            if (
                area.omraadeId === areaContainingSiida()?.omraadeId &&
                siidaStringArray.length > 0 &&
                districtsInArea.length === 0
            ) {
                return 1
            }
            return area.distrikter.length
        }
        const areaIncludingSiida = areaContainingSiida()
        if (
            !!areaIncludingSiida &&
            !areaStringArray.includes(
                areaIncludingSiida.omraadeId.toString()
            ) &&
            area.omraadeId === areaContainingSiida()?.omraadeId &&
            siidaStringArray.length > 0
        ) {
            return 1
        }
        return 0
    }

    const getOmraadeName = (omraadeId: number) => {
        return intl.formatMessage({ id: getOmraadeNameKey(omraadeId) })
    }

    const sortGeografi = (d1: Area, d2: Area) => {
        return getOmraadeName(d1.omraadeId) > getOmraadeName(d2.omraadeId)
            ? 1
            : -1
    }

    return (
        <Container
            maxWidth={false}
            sx={{
                display: 'flex',
                height: '100%',
                flexDirection: 'column',
            }}>
            <Box sx={{ minHeight: 'fill-available' }}>
                <NavigationHeader
                    onClickBack={() =>
                        navigate(
                            {
                                pathname: INNSYN_SEARCH_OEREMERKE_PATH,
                                search: `?${query}`,
                            },
                            { state: { isRightEar: state?.isRightEar } }
                        )
                    }
                />
                <Box
                    sx={{
                        marginTop: '1rem',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                    <div style={{ fontWeight: '400', fontSize: '1.25rem' }}>
                        <FormattedMessage id={'search-page.grazing-area'} />
                    </div>
                    <List
                        component='nav'
                        aria-label='liste over alle reinområder'
                        sx={{ marginBottom: '20%' }}>
                        {geografi &&
                            geografi
                                .sort(sortGeografi)
                                .map((omraade, index) => (
                                    <div key={'area-' + index}>
                                        <Stack direction='row'>
                                            <Checkbox
                                                inputProps={{
                                                    'aria-label': `Velg ${omraade.omraadeNavn}`,
                                                }}
                                                checked={
                                                    areaStringArray.includes(
                                                        omraade.omraadeId.toString()
                                                    ) ||
                                                    (omraade.omraadeId ===
                                                        areaContainingSiida()
                                                            ?.omraadeId &&
                                                        siidaStringArray.length >
                                                            0)
                                                }
                                                onChange={() =>
                                                    handleChange(
                                                        omraade.omraadeId
                                                    )
                                                }
                                            />
                                            <ListItem
                                                button
                                                onClick={() => {
                                                    selectDisctrict(
                                                        omraade.omraadeId
                                                    )
                                                }}>
                                                <ListItemText
                                                    primary={intl.formatMessage(
                                                        {
                                                            id: getOmraadeNameKey(
                                                                omraade.omraadeId
                                                            ),
                                                        }
                                                    )}
                                                />
                                                <Stack direction='row'>
                                                    {getNumberOfSelectedDistrictsForArea(
                                                        omraade
                                                    ) > 0 && (
                                                        <Box
                                                            sx={{
                                                                color: 'common.white',
                                                                backgroundColor:
                                                                    'primary.main',
                                                                borderRadius:
                                                                    '50%',
                                                                height: '20px',
                                                                width: '28px',
                                                                padding: '5px',
                                                                textAlign:
                                                                    'center',
                                                                fontSize:
                                                                    '1.125rem',
                                                                margin: '0 16px',
                                                            }}>
                                                            {getNumberOfSelectedDistrictsForArea(
                                                                omraade
                                                            )}
                                                        </Box>
                                                    )}
                                                    <ArrowForwardIosIcon />
                                                </Stack>
                                            </ListItem>
                                        </Stack>
                                        <Divider />
                                    </div>
                                ))}
                    </List>
                </Box>
            </Box>
            <Button
                size='medium'
                variant='contained'
                sx={{ position: 'fixed', marginBottom: '2rem' }}
                className='useButton'
                disabled={
                    areaStringArray.length === 0 &&
                    districtStringArray.length === 0 &&
                    query.getAll('s').length === 0
                }
                onClick={() =>
                    navigate(
                        {
                            pathname: INNSYN_SEARCH_OEREMERKE_PATH,
                            search: `?${query}`,
                        },
                        { state: { isRightEar: state?.isRightEar } }
                    )
                }>
                <FormattedMessage id={'search-page.use'} />
            </Button>
        </Container>
    )
}

export default SelectArea
